import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Alert, Button, Container, Form, Modal, ProgressBar, Spinner, Table } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eraser, ExternalLink, Eye, FileArchive, Trash2, Upload } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import CustomSelect from "../../components/form/CustomSelect";
import StatusChip from "../../components/statusChip/StatusChip";
import DeletePopup from "../../components/delete-popup";

/**
 * Importing a package of maps that were georeferenced elsewhere — first, the
 * village packages: one zip per taluka with the original JPGs, a GeoTIFF per
 * village, summary.csv and the village boundaries.
 *
 *   /maps-georeference/import       upload a package; recent imports
 *   /maps-georeference/import/:id   the check report, then the import
 *
 * The backend does the work (R-Point modules/v1/admin/maps/imports): it pairs
 * each village's files, finds where each GeoTIFF sits on its JPG, and on
 * import creates the sheets one at a time. Nothing is saved until "Import" is
 * pressed.
 *
 * Each village comes in one of three ways (the check suggests one; the admin
 * can change it):
 *   publish     georeferenced and tiled now: on the map
 *   draft       its suggested position is kept, but it stays off the map until
 *               someone checks and saves it (the "To check" tab)
 *   image_only  just the sheet, with no position, placed by hand later
 */

const CONFIDENCES = ["Good", "High", "Review"];
const DEFAULT_CONFIDENCES = ["Good", "High"];
const POLL_MS = 2500;

const ROW_STATUS = {
  ready: { bg: "primary", label: "Ready" },
  skipped: { bg: "light", text: "dark", label: "Skipped" },
  problem: { bg: "warning", label: "Problem" },
  importing: { bg: "info", label: "Importing..." },
  imported: { bg: "success", label: "Imported" },
  failed: { bg: "danger", label: "Failed" },
};

const IMPORT_STATUS = {
  checking: { bg: "info", label: "Checking..." },
  checked: { bg: "primary", label: "Checked" },
  check_failed: { bg: "danger", label: "Check failed" },
  importing: { bg: "info", label: "Importing..." },
  completed: { bg: "success", label: "Completed" },
  interrupted: { bg: "warning", label: "Interrupted" },
  queued: { bg: "secondary", label: "Queued" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "ready", label: "Ready" },
  { key: "imported", label: "Imported" },
  { key: "problem", label: "Problems" },
  { key: "failed", label: "Failed" },
  { key: "skipped", label: "Skipped" },
];

const MODES = [
  { value: "publish", label: "Live", bg: "success", hint: "Georeferenced and on the map straight away" },
  { value: "draft", label: "Draft", bg: "primary", hint: "Kept off the map until checked in the workspace" },
  { value: "image_only", label: "Image only", bg: "secondary", hint: "No position; placed by hand" },
];
const MODE_BY_VALUE = Object.fromEntries(MODES.map((m) => [m.value, m]));

const STAGE_LABELS = {
  unzipping: "Unzipping the package",
  reading: "Reading the package",
  matching: "Placing the images",
  importing: "Importing villages",
  cleaning: "Removing watermarks",
};

/**
 * Rows an import made before drafts existed left out: other ratings were
 * skipped ("Confidence Review is not imported.") and so were images missing
 * from summary.csv. Today the same package brings both in, as drafts or
 * image-only sheets to check.
 */
const skippedUnderOldRules = (r) =>
  r.status === "skipped" && /(is not imported\.$|this image was not georeferenced)/.test(r.reason || "");

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/**
 * What a waiting import is waiting to do, and its place in that line. The
 * server has two lines, checks and imports, each running one job at a time;
 * the next in a line starts by itself when the one before it finishes.
 */
const queueText = (r) => {
  if (r?.status !== "queued") return null;
  const action = r.queue?.action;
  const what = action === "apply" ? "Waiting to import" : action === "clean" ? "Waiting to remove the watermark" : "Waiting to be checked";
  return r.queue_position ? `${what} · ${ordinal(r.queue_position)} in line` : what;
};

/** Whether an import is doing something now or will soon (worth polling). */
const isBusy = (r) => Boolean(r?.running) || ["queued", "checking", "importing"].includes(r?.status);

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const formatSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatDate = (value) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-";

function Chip({ map, status }) {
  const s = map[status] || { bg: "secondary", label: status };
  return <StatusChip bg={s.bg} text={s.text} pill title={s.label} className={s.bg === "light" ? "border" : ""} />;
}

export default function ImportPackage() {
  const { importId } = useParams();
  const headerPortalTarget = document.getElementById("header-portal-target");

  const header = (
    <div className="d-flex align-items-center justify-content-between w-100 pe-3">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        Map Georeferences <span className="text-muted fw-normal">/ Import georeferenced maps</span>
      </h4>
    </div>
  );

  return (
    <>
      {headerPortalTarget && createPortal(header, headerPortalTarget)}
      <Container fluid className="px-0">
        {importId ? <ImportReport importId={importId} /> : <NewImport />}
      </Container>
    </>
  );
}

// ─── upload ─────────────────────────────────────────────────────────────────

function NewImport() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  // "Upload again" from an earlier import arrives with its type and taluka.
  const prefill = useLocation().state?.prefill || null;

  const [mapTypes, setMapTypes] = useState([]);
  const [mapTypeId, setMapTypeId] = useState(prefill?.mapTypeId ? String(prefill.mapTypeId) : "");
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [location, setLocation] = useState(prefill?.location || null);
  const [confidences, setConfidences] = useState(DEFAULT_CONFIDENCES);
  // Import as soon as the check is done, with the suggested modes.
  const [autoImport, setAutoImport] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(() => {
    Axios.get("/maps/imports", { params: { limit: 20 }, ...authHeaders() })
      .then((res) => setRecent(res.data?.data?.data || []))
      .catch((err) => console.error("Failed to fetch imports", err));
  }, []);

  // The list follows the queue while anything is waiting or running.
  const anyBusy = recent.some(isBusy);
  useEffect(() => {
    if (!anyBusy) return undefined;
    const timer = setInterval(loadRecent, POLL_MS);
    return () => clearInterval(timer);
  }, [anyBusy, loadRecent]);

  useEffect(() => {
    Axios.get("/maps/map-types/all?is_active=true&is_map_georeference=true", authHeaders())
      .then((res) => {
        const types = res.data?.data || [];
        setMapTypes(types);
        // Packages so far are village maps; start there when the type exists.
        const village = types.find((t) => t.code?.toLowerCase() === "village");
        if (village) setMapTypeId((current) => current || String(village.id));
      })
      .catch((err) => console.error("Failed to fetch map types", err));

    loadRecent();
  }, [loadRecent]);

  // Only the locations sheets can go under (the type's last level, e.g. talukas).
  useEffect(() => {
    if (!mapTypeId) {
      setLocationOptions([]);
      return undefined;
    }
    const handler = setTimeout(async () => {
      try {
        const res = await Axios.get("/maps/map-locations/all", {
          params: { map_type_id: mapTypeId, search: locationSearch, last_level_only: true, page: 1, limit: 50 },
          ...authHeaders(),
        });
        setLocationOptions(
          (res.data?.data || []).map((l) => ({ value: l.id, label: l.location_path || l.name, name: l.name }))
        );
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [mapTypeId, locationSearch]);

  const mapTypeOptions = useMemo(
    () => mapTypes.map((t) => ({ value: String(t.id), label: `${t.name} (${t.code?.toUpperCase()})` })),
    [mapTypes]
  );

  const toggleConfidence = (c) =>
    setConfidences((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.zip$/i.test(f.name)) {
      toast.error("Choose the package as a .zip file.");
      return;
    }
    setFile(f);
  };

  const canSubmit = mapTypeId && location && file && uploadPercent === null;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const body = new FormData();
    body.append("map_type_id", mapTypeId);
    body.append("location_id", location.value);
    body.append("confidences", JSON.stringify(confidences));
    body.append("auto_import", autoImport ? "true" : "false");
    body.append("file", file);

    setUploadPercent(0);
    try {
      const res = await Axios.post("/maps/imports", body, {
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => p.total && setUploadPercent(Math.round((p.loaded / p.total) * 100)),
      });
      const created = res.data?.data;
      toast.success(
        `${created?.file_name || "Package"} uploaded. It is checked${autoImport ? " and imported" : ""} in turn; you can upload the next one.`
      );
      // Stay here, ready for the next taluka's package; the list shows the queue.
      setFile(null);
      setLocation(null);
      setUploadPercent(null);
      loadRecent();
    } catch (err) {
      toast.error(errorMessage(err, "The upload failed."));
      setUploadPercent(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Button variant="link" className="p-0 d-flex align-items-center gap-1" onClick={() => navigate("/maps-georeference")}>
            <ArrowLeft size={16} /> Back to georeferences
          </Button>
        </div>

        <h5 className="fw-semibold mb-1">Import a georeferenced package</h5>
        {prefill && (
          <Alert variant="info" className="py-2 small" style={{ maxWidth: 720 }}>
            Uploading <span className="fw-medium">{prefill.fileName || "the package"}</span> again under{" "}
            <span className="fw-medium">{prefill.location?.label}</span>. Villages already on the map are left as they
            are; the rest come in as drafts or image-only sheets to check.
          </Alert>
        )}
        <p className="text-muted mb-4" style={{ maxWidth: 720 }}>
          Upload one taluka's package as a zip: the original village images, and a <code>georef</code> folder with{" "}
          <code>summary.csv</code>, <code>geotiff/</code> and <code>village_boundaries.geojson</code>. You'll see a check
          of every village before anything is saved.
        </p>

        <Form onSubmit={submit} style={{ maxWidth: 720 }}>
          <div className="row g-3">
            <div className="col-md-6">
              <CustomSelect
                label={{ name: "Map type" }}
                required
                options={mapTypeOptions}
                value={mapTypeOptions.find((o) => o.value === mapTypeId) || null}
                onChange={(o) => {
                  setMapTypeId(o?.value || "");
                  setLocation(null);
                }}
                placeholder="Choose a map type"
              />
            </div>
            <div className="col-md-6">
              <CustomSelect
                label={{ name: "Add the sheets under" }}
                required
                isDisabled={!mapTypeId}
                options={location && !locationOptions.some((o) => o.value === location.value) ? [location, ...locationOptions] : locationOptions}
                value={location}
                inputValue={locationSearch}
                onInputChange={(val, { action }) => {
                  if (action === "input-change") setLocationSearch(val);
                  else if (action === "menu-close") setLocationSearch("");
                }}
                onChange={(o) => setLocation(o || null)}
                isClearable
                placeholder="Search a taluka"
              />
            </div>
          </div>

          <Form.Group className="mt-4">
            <Form.Label className="text-primary">Put on the map straight away: villages rated</Form.Label>
            <div className="d-flex gap-4">
              {CONFIDENCES.map((c) => (
                <Form.Check
                  key={c}
                  id={`confidence-${c}`}
                  type="checkbox"
                  label={c}
                  checked={confidences.includes(c)}
                  onChange={() => toggleConfidence(c)}
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              Villages with other ratings come in as drafts: they stay off the map until someone checks them in
              the workspace. You can change this per village after the check.
            </Form.Text>
          </Form.Group>

          <Form.Check
            className="mt-3"
            id="import-auto"
            type="checkbox"
            checked={autoImport}
            onChange={(e) => setAutoImport(e.target.checked)}
            label="Import right after the check, with the suggested modes (the ratings above go live, the rest come in as drafts)"
          />

          <Form.Group className="mt-4">
            <Form.Label className="text-primary">Package (.zip)</Form.Label>
            <div
              role="button"
              tabIndex={0}
              className="border rounded-2 p-4 text-center"
              style={{ borderStyle: "dashed", cursor: "pointer", background: "var(--primary-50, #f8f9fb)" }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickFile(e.dataTransfer.files?.[0]);
              }}
            >
              {file ? (
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <FileArchive size={20} />
                  <span className="fw-medium">{file.name}</span>
                  <span className="text-muted">({formatSize(file.size)})</span>
                </div>
              ) : (
                <div className="text-muted">
                  <Upload size={20} className="mb-1" />
                  <div>Drop the zip here, or click to choose it</div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              className="d-none"
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </Form.Group>

          {uploadPercent !== null && (
            <div className="mt-3">
              <div className="small text-muted mb-1">Uploading... {uploadPercent}%</div>
              <ProgressBar now={uploadPercent} />
            </div>
          )}

          <Button type="submit" className="mt-4 d-flex align-items-center gap-2" disabled={!canSubmit}>
            {uploadPercent !== null ? <Spinner size="sm" /> : <Upload size={16} />}
            Upload and check
          </Button>
          <Form.Text className="text-muted d-block mt-2">
            Upload as many packages as you like. Checks and imports each have their own line, one at a time: while one
            package imports, the next ones are checked. Each job starts by itself when its turn comes.
          </Form.Text>
        </Form>
      </div>

      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <h6 className="fw-semibold mb-3">Recent imports</h6>
        {recent.length === 0 ? (
          <p className="text-muted mb-0">No imports yet.</p>
        ) : (
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Package</th>
                <th>Under</th>
                <th>Uploaded</th>
                <th>Status</th>
                <th>Villages</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/maps-georeference/import/${r.id}`)}>
                  <td className="fw-medium">{r.file_name}</td>
                  <td>
                    {r.location?.name || "-"} <span className="text-muted">({r.mapType?.code?.toUpperCase() || "-"})</span>
                  </td>
                  <td>{formatDate(r.created_at)}</td>
                  <td>
                    <Chip map={IMPORT_STATUS} status={r.status} />
                    {queueText(r) && <div className="small text-muted">{queueText(r)}</div>}
                  </td>
                  <td className="text-muted small">{countsText(r.counts)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
}

/** What happens (or happened) to a sheet that was already uploaded under the location. */
function existingNote(r) {
  const done = r.status === "imported";
  if (r.existing_action === "same_image") {
    return done
      ? `Georeferenced the existing sheet "${r.existing_title}".`
      : `Will georeference the existing sheet "${r.existing_title}" (same image).`;
  }
  return done
    ? `Imported into the existing sheet "${r.existing_title}"; its file was replaced with the package image.`
    : `Existing sheet "${r.existing_title}" is not georeferenced: its file will be replaced with the package image.`;
}

function countsText(counts = {}) {
  const order = ["imported", "ready", "failed", "problem", "skipped"];
  const parts = order.filter((k) => counts[k]).map((k) => `${counts[k]} ${ROW_STATUS[k].label.toLowerCase()}`);
  return parts.join(" · ") || "-";
}

// ─── report ─────────────────────────────────────────────────────────────────

function ImportReport({ importId }) {
  const navigate = useNavigate();
  const toast = useToast();

  const [record, setRecord] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  // Per-village changes to the suggested mode: { key: mode }.
  const [modeOverrides, setModeOverrides] = useState({});
  const [trim, setTrim] = useState(true);
  const [removeWatermark, setRemoveWatermark] = useState(false);
  // { mode: "import" | "imported", key } while the before/after preview is open.
  const [preview, setPreview] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  // Selection is set once, when the report first has rows to pick from.
  const selectionReadyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await Axios.get(`/maps/imports/${importId}`, authHeaders());
      setRecord(res.data?.data || null);
      setLoadError("");
    } catch (err) {
      setLoadError(errorMessage(err, "Could not load this import."));
    }
  }, [importId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while the check or import runs, or waits its turn.
  const busy = isBusy(record);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  const rows = useMemo(() => record?.rows || [], [record]);
  const importable = useCallback((r) => r.status === "ready" || r.status === "failed", []);

  useEffect(() => {
    // Wait for the check: a package still in the queue has no rows yet.
    if (selectionReadyRef.current || !record || record.running || ["queued", "checking"].includes(record.status)) return;
    selectionReadyRef.current = true;
    setSelected(new Set(rows.filter(importable).map((r) => r.key)));
  }, [record, rows, importable]);

  const visible = useMemo(() => (filter === "all" ? rows : rows.filter((r) => r.status === filter)), [rows, filter]);
  const counts = record?.counts || {};
  const running = Boolean(record?.running);
  const selectable = !running && ["checked", "interrupted", "completed"].includes(record?.status);
  const chosen = rows.filter((r) => importable(r) && selected.has(r.key));
  const modeOf = (r) => modeOverrides[r.key] || r.mode || "publish";
  const chosenByMode = chosen.reduce((acc, r) => ({ ...acc, [modeOf(r)]: (acc[modeOf(r)] || 0) + 1 }), {});
  const imported = rows.filter((r) => r.status === "imported");
  const waitingToCheck = imported.filter((r) => r.mode && r.mode !== "publish").length;

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleImportable = visible.filter(importable);
  const allVisibleSelected = visibleImportable.length > 0 && visibleImportable.every((r) => selected.has(r.key));
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleImportable.forEach((r) => (allVisibleSelected ? next.delete(r.key) : next.add(r.key)));
      return next;
    });

  const startImport = async () => {
    if (!chosen.length) return;
    setStarting(true);
    try {
      const res = await Axios.post(
        `/maps/imports/${importId}/apply`,
        {
          keys: chosen.map((r) => r.key),
          // Only the villages whose mode was changed here.
          modes: Object.fromEntries(
            chosen.filter((r) => modeOverrides[r.key] && modeOverrides[r.key] !== r.mode).map((r) => [r.key, modeOverrides[r.key]])
          ),
          trim,
          remove_watermark: removeWatermark,
        },
        authHeaders()
      );
      setRecord(res.data?.data || null);
      setModeOverrides({});
      toast.success(
        `Import of ${chosen.length} ${chosen.length === 1 ? "village" : "villages"} queued. It starts as soon as no other import is running.`
      );
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not start the import."));
    } finally {
      setStarting(false);
    }
  };

  // Imported villages that still carry the watermark.
  const watermarked = rows.filter((r) => r.status === "imported" && r.map_id && !r.watermark_removed);

  const startCleaning = async () => {
    setCleaning(true);
    try {
      const res = await Axios.post(
        `/maps/imports/${importId}/remove-watermark`,
        { keys: watermarked.map((r) => r.key) },
        authHeaders()
      );
      setRecord(res.data?.data || null);
      setPreview(null);
      toast.success(
        `Watermark removal for ${watermarked.length} ${watermarked.length === 1 ? "village" : "villages"} queued. It starts as soon as no other import is running.`
      );
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not start removing the watermark."));
    } finally {
      setCleaning(false);
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const cancelQueued = async () => {
    setCancelling(true);
    try {
      const res = await Axios.post(`/maps/imports/${importId}/cancel`, {}, authHeaders());
      if (res.data?.data?.removed) {
        toast.success("Upload cancelled; the package was removed.");
        navigate("/maps-georeference/import");
        return;
      }
      setRecord(res.data?.data || null);
      toast.success("Taken out of the queue.");
      load();
    } catch (err) {
      toast.error(errorMessage(err, "Could not cancel."));
      load();
    } finally {
      setCancelling(false);
    }
  };

  const remove = async () => {
    try {
      await Axios.delete(`/maps/imports/${importId}`, authHeaders());
      toast.success("Import removed. Sheets already imported stay.");
      navigate("/maps-georeference/import");
    } catch (err) {
      toast.error(errorMessage(err, "Could not remove the import."));
    } finally {
      setShowDelete(false);
    }
  };

  if (loadError && !record) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Alert variant="danger" className="mb-3">{loadError}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate("/maps-georeference/import")}>
          Back to imports
        </Button>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-5 text-center">
        <Spinner />
      </div>
    );
  }

  const oldRuleSkips = rows.filter(skippedUnderOldRules);
  const uploadAgain = () =>
    navigate("/maps-georeference/import", {
      state: {
        prefill: {
          mapTypeId: record.map_type_id,
          location: record.location ? { value: record.location.id, label: record.location.name, name: record.location.name } : null,
          fileName: record.file_name,
        },
      },
    });

  const progress = record.progress;
  const progressPercent = progress?.of ? Math.round((progress.done / progress.of) * 100) : null;
  const mapCode = record.mapType?.code?.toLowerCase();

  return (
    <div className="bg-white rounded-2 common-shadow mt-4 p-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Button variant="link" className="p-0 d-flex align-items-center gap-1" onClick={() => navigate("/maps-georeference/import")}>
          <ArrowLeft size={16} /> All imports
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          className="d-flex align-items-center gap-1"
          disabled={running}
          onClick={() => setShowDelete(true)}
        >
          <Trash2 size={14} /> Remove import
        </Button>
      </div>

      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
        <h5 className="fw-semibold mb-0">{record.file_name}</h5>
        <Chip map={IMPORT_STATUS} status={record.status} />
      </div>
      <p className="text-muted mb-3">
        {record.mapType?.name || "-"} · sheets go under <span className="fw-medium text-body">{record.location?.name || "-"}</span>
        {" · "}uploaded {formatDate(record.created_at)}
      </p>

      {running && (
        <div className="mb-4" style={{ maxWidth: 560 }}>
          <div className="small text-muted mb-1 d-flex align-items-center gap-2">
            <Spinner size="sm" />
            {STAGE_LABELS[progress?.stage] || "Working"}
            {progress?.of ? ` — ${progress.done} of ${progress.of}` : "..."}
          </div>
          <ProgressBar now={progressPercent ?? 100} animated={progressPercent === null} striped={progressPercent === null} />
          {record.status === "importing" && (
            <div className="small text-muted mt-1">
              Each village is uploaded in turn; live ones are also georeferenced and tiled. You can leave this page;
              the import carries on.
            </div>
          )}
        </div>
      )}

      {record.status === "queued" && (
        <Alert variant="secondary" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span>
            <span className="fw-medium">{queueText(record)}.</span>{" "}
            {record.queue?.action === "check"
              ? "Packages are checked one at a time (alongside any import that is running);"
              : "Imports run one at a time;"}{" "}
            this one starts by itself when the ones ahead of it finish. You can leave this page.
            {record.queue?.action === "check" && record.options?.auto_import && " It will be imported straight after its check."}
          </span>
          <Button size="sm" variant="outline-secondary" onClick={cancelQueued} disabled={cancelling}>
            {cancelling ? <Spinner size="sm" /> : record.queue?.action === "check" ? "Cancel upload" : "Take out of queue"}
          </Button>
        </Alert>
      )}

      {record.status === "check_failed" && (
        <Alert variant="danger">
          <div className="fw-medium">The package could not be checked.</div>
          <div>{record.message}</div>
          <Button variant="outline-danger" size="sm" className="mt-2" onClick={() => navigate("/maps-georeference/import")}>
            Upload again
          </Button>
        </Alert>
      )}
      {record.status === "interrupted" && (
        <Alert variant="warning">
          The server restarted while this was running. Villages already imported are kept; select the rest and import
          again to finish.
        </Alert>
      )}
      {record.status === "completed" && !running && (
        <Alert variant="success" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span>
            {rows.some(skippedUnderOldRules) ? "All selected villages are imported." : "All villages are imported."}
            {waitingToCheck > 0 &&
              (waitingToCheck === imported.length
                ? ` None are on the map yet: ${waitingToCheck === 1 ? "it is" : `all ${waitingToCheck} are`} waiting in To check.`
                : ` ${imported.length - waitingToCheck} ${imported.length - waitingToCheck === 1 ? "is" : "are"} on the map; ${waitingToCheck} ${
                    waitingToCheck === 1 ? "is" : "are"
                  } waiting in To check.`)}
          </span>
          {waitingToCheck > 0 && (
            <Button size="sm" variant="outline-success" onClick={() => navigate("/maps-georeference", { state: { status: "to_check" } })}>
              Open To check
            </Button>
          )}
        </Alert>
      )}
      {record.status === "checked" && record.message && !running && <Alert variant="warning">{record.message}</Alert>}
      {oldRuleSkips.length > 0 && !running && (
        <Alert variant="info" className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span style={{ maxWidth: 760 }}>
            {oldRuleSkips.length} {oldRuleSkips.length === 1 ? "village was" : "villages were"} skipped under the earlier
            rule that imported only the chosen ratings. Upload the package again to bring{" "}
            {oldRuleSkips.length === 1 ? "it" : "them"} in as drafts (or image-only sheets) to check; villages already on
            the map are left as they are.
          </span>
          <Button size="sm" variant="outline-primary" className="d-flex align-items-center gap-1" onClick={uploadAgain}>
            <Upload size={14} /> Upload again
          </Button>
        </Alert>
      )}

      {rows.length > 0 && (
        <>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            {FILTERS.map((f) => {
              const n = f.key === "all" ? rows.length : counts[f.key] || 0;
              if (f.key !== "all" && !n) return null;
              return (
                <Button
                  key={f.key}
                  size="sm"
                  variant={filter === f.key ? "primary" : "outline-secondary"}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label} ({n})
                </Button>
              );
            })}
          </div>

          <div className="table-responsive" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table hover size="sm" className="align-middle mb-0">
              <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                <tr>
                  <th style={{ width: 36 }}>
                    {selectable && visibleImportable.length > 0 && (
                      <Form.Check
                        type="checkbox"
                        aria-label="Select all shown"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                      />
                    )}
                  </th>
                  <th>Village</th>
                  <th>Census code</th>
                  <th>Confidence</th>
                  <th title="How clearly the GeoTIFF matched one place on the image, and how many times stronger than the next best place">
                    Match
                  </th>
                  <th>Status</th>
                  <th title="Live: on the map now. Draft: off the map until checked. Image only: placed by hand.">Import as</th>
                  <th>Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.key}>
                    <td>
                      {selectable && importable(r) && (
                        <Form.Check
                          type="checkbox"
                          aria-label={`Import ${r.title || r.village}`}
                          checked={selected.has(r.key)}
                          onChange={() => toggle(r.key)}
                        />
                      )}
                    </td>
                    <td>
                      <div className="fw-medium">{r.title || r.village}</div>
                      {r.title && r.village && r.title !== r.village && (
                        <div className="small text-muted">file: {r.village}</div>
                      )}
                    </td>
                    <td className="small">{r.census_code || "-"}</td>
                    <td>{r.confidence || "-"}</td>
                    <td className="small">
                      {typeof r.match === "number"
                        ? `${r.match.toFixed(2)}${r.runner_up > 0 ? ` (${(r.match / r.runner_up).toFixed(1)}×)` : ""}`
                        : "-"}
                    </td>
                    <td>
                      <Chip map={ROW_STATUS} status={r.status} />
                    </td>
                    <td>
                      <ModeCell
                        row={r}
                        mode={modeOf(r)}
                        editable={selectable && importable(r)}
                        onChange={(mode) => setModeOverrides((prev) => ({ ...prev, [r.key]: mode }))}
                      />
                    </td>
                    <td className="small" style={{ maxWidth: 360 }}>
                      {r.reason && <div>{r.reason}</div>}
                      {(r.review_reasons || []).map((w) => (
                        <div key={w} className="text-primary-emphasis">{w}</div>
                      ))}
                      {r.existing_action && <div className="text-primary">{existingNote(r)}</div>}
                      {r.watermark_removed && <div className="text-success">Watermark removed.</div>}
                      {r.message && <div className="text-danger">{r.message}</div>}
                      {(r.warnings || []).map((w) => (
                        <div key={w} className="text-warning-emphasis">{w}</div>
                      ))}
                    </td>
                    <td className="text-end">
                      {r.map_id && r.status === "imported" && mapCode && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 d-inline-flex align-items-center gap-1"
                          onClick={() => navigate(`/maps/${mapCode}/georeference/${r.map_id}`)}
                        >
                          Open <ExternalLink size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {selectable && rows.some(importable) && (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4 pt-3 border-top">
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  id="import-trim"
                  type="checkbox"
                  checked={trim}
                  onChange={(e) => setTrim(e.target.checked)}
                  label="Trim live sheets to their village boundary (hides neighbouring land, legend and title block)"
                />
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    id="import-watermark"
                    type="checkbox"
                    checked={removeWatermark}
                    onChange={(e) => setRemoveWatermark(e.target.checked)}
                    label="Remove the watermark (the pale logo stamped on the images)"
                  />
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 d-inline-flex align-items-center gap-1"
                    onClick={() => setPreview({ mode: "import", key: (chosen[0] || rows.find(importable))?.key })}
                  >
                    <Eye size={14} /> Preview
                  </Button>
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-1">
                <Button className="d-flex align-items-center gap-2" disabled={!chosen.length || starting} onClick={startImport}>
                  {starting ? <Spinner size="sm" /> : <Upload size={16} />}
                  Import {chosen.length} {chosen.length === 1 ? "village" : "villages"}
                </Button>
                {chosen.length > 0 && (
                  <span className="small text-muted">
                    {MODES.filter((m) => chosenByMode[m.value])
                      .map((m) => `${chosenByMode[m.value]} ${m.label.toLowerCase()}`)
                      .join(" · ")}
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!running && watermarked.length > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-4 pt-3 border-top">
          <div className="text-muted small" style={{ maxWidth: 520 }}>
            {watermarked.length} imported {watermarked.length === 1 ? "village still has" : "villages still have"} the
            watermark. Removing it replaces each sheet's image with a cleaned copy (and re-tiles the live ones in
            place); positions and trims stay the same.
          </div>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2"
            onClick={() => setPreview({ mode: "imported", key: watermarked[0].key })}
          >
            <Eraser size={16} /> Remove watermark from {watermarked.length}{" "}
            {watermarked.length === 1 ? "village" : "villages"}
          </Button>
        </div>
      )}

      <WatermarkPreview
        importId={importId}
        state={preview}
        choices={preview?.mode === "imported" ? watermarked : rows.filter((r) => importable(r) || r.status === "imported")}
        onChange={(key) => setPreview((p) => ({ ...p, key }))}
        onHide={() => setPreview(null)}
        action={
          preview?.mode === "imported"
            ? {
                label: `Remove from ${watermarked.length} ${watermarked.length === 1 ? "village" : "villages"}`,
                busy: cleaning,
                run: startCleaning,
              }
            : {
                label: "Use watermark removal for this import",
                busy: false,
                run: () => {
                  setRemoveWatermark(true);
                  setPreview(null);
                },
              }
        }
      />

      <DeletePopup
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={remove}
        title="Remove this import?"
        content="This removes the import and its uploaded files. Villages already imported stay on the map."
        saveTitle="Remove"
      />
    </div>
  );
}

/** How a village comes in: a picker while it can still be changed, a label after. */
function ModeCell({ row, mode, editable, onChange }) {
  if (!row.mode && row.status !== "imported") return <span className="text-muted">-</span>;
  if (!editable) {
    const m = MODE_BY_VALUE[row.mode || "publish"];
    return <StatusChip bg={m.bg} pill title={m.label} />;
  }
  return (
    <Form.Select
      size="sm"
      value={mode}
      onChange={(e) => onChange(e.target.value)}
      aria-label={`Import ${row.title || row.village} as`}
      title={MODE_BY_VALUE[mode]?.hint}
      style={{ minWidth: 120 }}
    >
      {MODES.map((m) => (
        // Live and Draft need the position the check found.
        <option key={m.value} value={m.value} disabled={m.value !== "image_only" && !row.placed}>
          {m.label}
        </option>
      ))}
    </Form.Select>
  );
}

/**
 * Before/after of one village with the watermark removed, so the result can
 * be judged before it is applied. The backend renders both (see
 * import.service.js previewWatermark).
 */
function WatermarkPreview({ importId, state, choices, onChange, onHide, action }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAfter, setShowAfter] = useState(true);
  const key = state?.key;

  useEffect(() => {
    if (!key) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    Axios.get(`/maps/imports/${importId}/watermark-preview`, { params: { key }, ...authHeaders() })
      .then((res) => !cancelled && setData(res.data?.data || null))
      .catch((err) => !cancelled && setError(errorMessage(err, "Could not make the preview.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [importId, key]);

  const options = choices.map((r) => ({ value: r.key, label: r.title || r.village }));

  return (
    <Modal show={Boolean(state)} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">Watermark removal preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-end flex-wrap gap-3 mb-3">
          <div style={{ minWidth: 260 }}>
            <CustomSelect
              label={{ name: "Village" }}
              options={options}
              value={options.find((o) => o.value === key) || null}
              onChange={(o) => o && onChange(o.value)}
            />
          </div>
          <div className="btn-group" role="group" aria-label="Before or after">
            <Button variant={!showAfter ? "primary" : "outline-secondary"} size="sm" onClick={() => setShowAfter(false)}>
              Before
            </Button>
            <Button variant={showAfter ? "primary" : "outline-secondary"} size="sm" onClick={() => setShowAfter(true)}>
              After
            </Button>
          </div>
          {data && <span className="text-muted small">{data.changed_pct}% of the image changed</span>}
        </div>

        {loading && (
          <div className="text-center p-5">
            <Spinner />
          </div>
        )}
        {error && !loading && <Alert variant="danger">{error}</Alert>}
        {data && !loading && (
          <>
            <div className="border rounded-2 overflow-auto text-center bg-white" style={{ maxHeight: "65vh" }}>
              <img
                src={showAfter ? data.after : data.before}
                alt={`${data.village} ${showAfter ? "after" : "before"} removing the watermark`}
                style={{ maxWidth: "100%" }}
              />
            </div>
            <div className="small text-muted mt-2">
              Check that plot lines, survey numbers and red roads are still there in "After". Only pale, washed-out
              colours are removed.
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button onClick={action.run} disabled={action.busy || loading || Boolean(error)} className="d-flex align-items-center gap-2">
          {action.busy && <Spinner size="sm" />}
          {action.label}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
